// Native & external imports
import { Injectable, Renderer2, RendererFactory2, OnDestroy, inject } from '@angular/core';
import { Subject, fromEventPattern } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import 'moment-timezone';
import * as moment from 'moment';
import * as XRegExp from 'xregexp';
// Objects
import { currencyList } from '../objects/CurrencyData.object';

/**
 * Minimal shape of tenant app settings consumed by {@link AppLocalization.updateAccount}.
 * Declared locally so this shared service does not depend on any app-level object; the
 * per-app CmpAppConfigObject is structurally compatible and can be passed directly.
 */
export interface AppLocaleSettings {
  locale?: string;
  timeZone?: string;
  currencyName?: string;
}

//#region Root level stuff

const StringTuple = <T extends string[]>(...args: T) => args;
const MonthYearFormatsArray = StringTuple('MMMM YYYY', 'MMM YYYY', 'MYY');
const DayMonthFormatsArray = StringTuple('LLS', 'lls', 'md', 'MYY');
const CompoundLocalizedDateTimeFormatsArray = StringTuple('L LT', 'l lt', 'mdy');
const MomentLocalizedFormatsArray = StringTuple('LTS', 'LT', 'L', 'LL', 'LLL', 'LLLL', 'lts', 'lt', 'l', 'll', 'lll', 'llll');
const APIFormatsArray = StringTuple('yyyy-MM-dd', 'yyyy', 'd');
type MonthYearFormats = typeof MonthYearFormatsArray[number];
type DayMonthFormats = typeof DayMonthFormatsArray[number];
type CompoundLocalizedDateTimeFormats = typeof CompoundLocalizedDateTimeFormatsArray[number];
type MomentLocalizedFormats = typeof MomentLocalizedFormatsArray[number];
type APIFormats = typeof APIFormatsArray[number];
export type LocalizedDateTimeFormat = MomentLocalizedFormats | MonthYearFormats |
  DayMonthFormats | CompoundLocalizedDateTimeFormats | APIFormats;
export function isLocalizedDateTimeFormat(value: string): value is LocalizedDateTimeFormat {
  return (MonthYearFormatsArray as Array<string>)
    .concat(DayMonthFormatsArray)
    .concat(CompoundLocalizedDateTimeFormatsArray)
    .concat(MomentLocalizedFormatsArray)
    .concat(APIFormatsArray)
    .includes(value);
}
export type NumberingSystem =
  'arab' | 'arabext' | ' bali' | 'beng' | 'deva' | 'fullwide' | ' gujr' |
  'guru' | 'hanidec' | 'khmr' | ' knda' | 'laoo' | 'latn' | 'limb' |
  'mlym' | ' mong' | 'mymr' | 'orya' | 'tamldec' | ' telu' | 'thai' | 'tibt';
export type FormattableDateType = Date | moment.Moment | string | number;

//#endregion

  /**
   * Represents a parsed language tag (locale).
   * This class is meant to be compatible with Intl.Locale, for when it is not in the browser.
   */
  export class IntlLocalePolyfill implements Intl.Locale {
    // parsed language tag parts
    private _lt_language = '';
    private _lt_isGrandfatheredLanguage = false;
    private _lt_languageExt = '';
    private _lt_languageScript = '';
    private _lt_region = '';
    private _lt_variants: Array<string> = [];
    private _lt_extensions: Array<{ prefix: string, subtags: Array<string> }> = [];
    private _lt_privateUse: Array<string> = [];

    /**
     * Determines if the browser supports `Intl.Locale` and returns the native instance or the polyfill implementation.
     *
     * @remarks
     * All modern browsers implement `Intl.Locale`, but IE11 and below do not.  I am not sure we even support those older browsers.
     *
     * @param tag - The tag to parse.
     * @param options - A set of options to override unicode values in the tag.
     */
    public static LocaleFactory(tag: string, options?: Intl.LocaleOptions): Intl.Locale {
      try {
        if ('Locale' in Intl) {
          return new Intl.Locale(tag, options);
        } else {
          return new IntlLocalePolyfill(tag, options);
        }
      } catch (error) {
        // `tag` is often unvalidated input — navigator.languages[0]/navigator.language
        // (browser/OS/extension controlled) or a tenant's stored appSettings.locale.
        // A malformed BCP-47 tag makes Intl.Locale throw RangeError; fall back to the
        // default locale instead of crashing bootstrap / locale updates ('en-US' is always valid).
        console.warn(`[localization] Invalid locale tag "${tag}" — falling back to en-US.`, error);
        if ('Locale' in Intl) {
          return new Intl.Locale('en-US');
        } else {
          return new IntlLocalePolyfill('en-US');
        }
      }
    }

    /**
     * Returns basic, core information about the `Locale` in the form of a substring of the complete data string.
     */
    public get baseName(): string {
      return this._trimDashes(
        this._lt_language +
        (this._lt_languageExt !== '' ? '-' + this._lt_languageExt : '') +
        (this._lt_languageScript !== '' ? '-' + this._lt_languageScript : '') +
        (this._lt_region !== '' ? '-' + this._lt_region : '') +
        (this._lt_extensions.length > 0 ? '-' + this._lt_extensions.map((v) => v.prefix + '-' + v.subtags.join('-')).join('-') : '')
      );
    }

    /**
     * Returns the part of the `Locale` that indicates the Locale's calendar era.
     */
    public get calendar(): string | undefined {
      return this._getExtension('u', 'ca');
    }

    /**
     * Returns whether case is taken into account for the locale's collation rules.
     */
    public get caseFirst(): 'upper' | 'lower' | 'false' | undefined {
      const kf = this._getExtension('u', 'kf');
      if (kf === 'upper' || kf === 'lower' || kf === 'false') {
        return kf;
      }
      return undefined;
    }

    /**
     * Returns the collation type for the `Locale`, which is used to order strings according to the locale's rules.
     */
    public get collation(): string | undefined {
      return this._getExtension('u', 'co');
    }

    /**
     * Returns the time keeping format convention used by the locale.
     */
    public get hourCycle(): 'h11' | 'h12' | 'h23' | 'h24' | undefined {
      const hc = this._getExtension('u', 'hc');
      if (hc === 'h11' || hc === 'h12' || hc === 'h23' || hc === 'h24') {
        return hc;
      }
      return undefined;
    }

    /**
     * Returns the language associated with the locale.
     */
    public get language() {
      return this._trimDashes(
        this._lt_language +
        (this._lt_languageExt !== '' ? '-' + this._lt_languageExt : '')
      );
    }

    /**
     * Returns the numeral system used by the locale.
     */
    public get numberingSystem(): string | undefined {
      return this._getExtension('u', 'nu');
    }

    /**
     * Returns whether the locale has special collation handling for numeric characters.
     */
    public get numeric(): boolean | undefined {
      const kn = this._getExtension('u', 'kn', 'true');
      if (typeof kn !== 'undefined') {
        return kn !== 'false'; // `false` is the only actual value that kn can have so we check against that.
      }
      return undefined;
    }

    /**
     * Returns the region of the world (usually a country) associated with the locale.
     */
    public get region(): string | undefined {
      return this._lt_region.length === 0 ? undefined : this._lt_region;
    }

    /**
     * Returns the script used for writing the particular language used in the locale.
     */
    public get script(): string | undefined {
      return this._lt_languageScript.length === 0 ? undefined : this._lt_languageScript;
    }

    /**
     * Parse the language tag.
     *
     * @param tag - The tag to parse.
     * @param options - A set of options to override unicode values in the tag.
     */
    public constructor(tag: string, options?: Intl.LocaleOptions) {
      this._parseTag(tag, options);
    }

    /**
     * Gets the most likely values for the language, script, and region of the locale based on existing values.
     *
     * @remarks
     * __THIS DOES NOT PRODUCE THE SAME OUTPUT AS THE BUILT IN `Intl.Local`.__ To minimize or maximize
     * the locale would have to be compared to a lookup table (see: https://www.unicode.org/reports/tr35/#Likely_Subtags).
     * That really is beyond the scope of this.
     */
    public maximize(): IntlLocalePolyfill {
      return new IntlLocalePolyfill(this.toString());
    }

    /**
     * Gets the most likely values for the language, script, and region of the locale based on existing values.
     *
     * @remarks
     * __THIS DOES NOT PRODUCE THE SAME OUTPUT AS THE BUILT IN `Intl.Local`.__ To minimize or maximize
     * the locale would have to be compared to a lookup table (see: https://www.unicode.org/reports/tr35/#Likely_Subtags).
     * That really is beyond the scope of this.
     */
    public minimize(): IntlLocalePolyfill {
      return new IntlLocalePolyfill(this.baseName);
    }

    /**
     * Returns the Locale's full locale identifier string.
     *
     * @returns The tag after parsing and reforming.
     */
    public toString(): string  {
      const lt = this._lt_language +
      (this._lt_languageExt !== '' ? '-' + this._lt_languageExt : '') +
      (this._lt_languageScript !== '' ? '-' + this._lt_languageScript : '') +
      (this._lt_region !== '' ? '-' + this._lt_region : '') +
      (this._lt_variants.length > 0 ? '-' + this._lt_variants.join('-') : '') +
      (this._lt_extensions.length > 0 ? '-' + this._lt_extensions.map((v) => v.prefix + '-' + v.subtags.join('-')).join('-') : '') +
      (this._lt_privateUse.length > 0 ? '-x-' + this._lt_privateUse.join('-') : '');
      return this._trimDashes(lt);
    }

    /**
     * Parse the value and set the internal parts.
     *
     * @param tag - The tag to parse.
     * @param options - a set of options to override unicode values in the tag.
     */
    private _parseTag(tag: string, options?:  Intl.LocaleOptions) {
      this._lt_language = '';
      this._lt_isGrandfatheredLanguage = false;
      this._lt_languageExt = '';
      this._lt_languageScript = '';
      this._lt_region = '';
      this._lt_variants = [];
      this._lt_extensions = [];
      this._lt_privateUse = [];

      XRegExp.install({ namespacing: true });
      XRegExp.uninstall({ astral: true });

      // language tag parsed as per BCP47: https://tools.ietf.org/html/bcp47
      const languageTagRegEx = XRegExp(
        [
          '^\\s*(',
            // if it starts with "x-" the entire thing is private use. https://tools.ietf.org/html/bcp47#section-2.2.7
            '(?<PrivateUseOnly>[xX]-.*)|',
            '(',
              // the primary language subtag https://tools.ietf.org/html/bcp47#section-2.2.1
              '(?<Language>',
                // some primary language subtags do not conform https://tools.ietf.org/html/bcp47#section-2.2.8
                // and https://tools.ietf.org/html/bcp47#section-2.1
                '(?<GrandfatheredLanguage>',
                  '(en-GB-oed)|',
                  '(i-[a-z]+)|',
                  '(sgn-(BE-(FR|NL)|CH-DE))|',
                  '(art-lojban)|',
                  '(cel-gaulish)|',
                  '(no-(bok|nyn))|',
                  '(zh-(guoyu|hakka|min(-nan)?|xiang))|',
                  '(q[a-t][a-z])',
                ')|',
                '([a-z]{2,8})',
              ')',
              // extended langauge subtag https://tools.ietf.org/html/bcp47#section-2.2.2
              '(-(?<ExtendedLanguage>[a-z]{3})(?=\b))?',
              // script subtag https://tools.ietf.org/html/bcp47#section-2.2.3
              '(-(?<Script>[A-Z][a-z]{3}))?',
              // region subtag https://tools.ietf.org/html/bcp47#section-2.2.4
              '(-(?<Region>([A-Z]{2})|\\d{3}))?',
              // variant subtags https://tools.ietf.org/html/bcp47#section-2.2.5
              '(?<Variants>(-([a-zA-z][a-zA-Z0-9]{4,7}|[0-9][a-zA-Z0-9]{3,7}))*)',
              // extension subtags https://tools.ietf.org/html/bcp47#section-2.2.6
              '(?<Extensions>(-[a-wyzA-WYZ0-9](-[a-zA-Z0-9]{2,8})+)*)',
              // private use subtags https://tools.ietf.org/html/bcp47#section-2.2.7
              '(?<PrivateUse>-[xX](-[a-zA-Z0-9]{1,8})+)?',
            ')',
          ')\\s*$'
        ].join(''),
        'gi' // global, case-insensitive (BCP47 recommends _but does not require_ casing on elements of the language tag)
      );

      // When casing tag in the local strings, use en-US locale!!!! This prevents special casing
      // issues as specified in http://unicode.org/Public/UNIDATA/SpecialCasing.txt

      const matches = XRegExp.exec(tag, languageTagRegEx);
      if (matches && matches.groups) {
        this._lt_language = (
          matches.groups['Language'] ?
          matches.groups['Language'].toLowerCase() :
          ''
        );

        this._lt_isGrandfatheredLanguage = !!matches.groups['GrandfatheredLanguage'];

        this._lt_languageExt = (
          matches.groups['ExtendedLanguage'] ?
          matches.groups['ExtendedLanguage'].toLowerCase() :
          ''
        );

        this._lt_languageScript = (
          matches.groups['Script'] ?
          matches.groups['Script'].charAt(0).toUpperCase() +
          matches.groups['Script'].slice(1).toLowerCase() :
          ''
        );

        this._lt_region = (
          matches.groups['Region'] ?
          matches.groups['Region'].toUpperCase()
          : ''
        );

        if (matches.groups['Variants']) {
          this._lt_variants = matches.groups['Variants'].split('-');
        }

        if (matches.groups['Extensions']) {
          this._lt_extensions =
            XRegExp.split(matches.groups['Extensions'], /-[a-wyzA-WYZ0-9](-[a-zA-Z0-9]{2,8})+/gi)
            .map(function (v) {
              return { prefix: v.charAt(1), subtags: v.slice(3).split('-') };
            });
        }

        if (matches.groups['PrivateUseOnly']) {
          this._lt_privateUse = matches.groups['PrivateUseOnly'].slice(2).split('-');
        } else if (matches.groups['PrivateUse']) {
          this._lt_privateUse = matches.groups['PrivateUse'].slice(3).split('-');
        }
      }

      if (options) {
        // we have some overrides
        if (typeof options.calendar !== 'undefined' && options.calendar.length > 0 && options.calendar.length <= 8) {
          this._setExtension('u', 'ca', options.calendar);
        }
        if (typeof options.caseFirst !== 'undefined' && options.caseFirst.length > 0 && options.caseFirst.length <= 8) {
          this._setExtension('u', 'kf', options.caseFirst);
        }
        if (typeof options.collation !== 'undefined' && options.collation.length > 0 && options.collation.length <= 8) {
          this._setExtension('u', 'co', options.collation);
        }
        if (typeof options.hourCycle !== 'undefined' && options.hourCycle.length > 0 && options.hourCycle.length <= 8) {
          this._setExtension('u', 'hc', options.hourCycle);
        }
        if (typeof options.numberingSystem !== 'undefined' && options.numberingSystem.length > 0 && options.numberingSystem.length <= 8) {
          this._setExtension('u', 'nu', options.numberingSystem);
        }
        if (typeof options.numeric !== 'undefined') {
          this._setExtension('u', 'kn', options.numeric ? undefined : 'false');
        }
      }
    }

    /**
     * Sets an extension value.
     *
     * @param prefix - The prifix to look for in extenstions
     * @param subtag - The subtag to look for.  The `value` is going to be set to the subtag after this one.
     * @param value - The value to set to the subtag after `subtag`.
     */
    private _setExtension(prefix: string, subtag: string, value: string | undefined): void {
      if (this._lt_extensions.length > 0 && this._lt_extensions.some(v => v.prefix === prefix)) {
        this._lt_extensions = this._lt_extensions.map(v => {
          if (v.prefix === prefix) {
            let subtagIdx: number;
            if (v.subtags.length > 0 && (subtagIdx = v.subtags.indexOf(subtag)) > -1) {
              if (v.subtags.length > subtagIdx + 1) {
                if (typeof value === 'undefined' && v.subtags[subtagIdx + 1].length > 2) {
                  v.subtags.splice(subtagIdx + 1, 1);
                } else if (typeof value !== 'undefined') {
                  v.subtags[subtagIdx + 1] = value;
                }
              } else if (typeof value !== 'undefined') {
                // the last item was <subtag>
                v.subtags.push(value);
              }
            } else {
              v.subtags.push(subtag);
              if (typeof value !== 'undefined') {
                v.subtags.push(value);
              }
            }
          }
          return v;
        });
      } else {
        this._lt_extensions.push({
          prefix: prefix,
          subtags: (typeof value !== 'undefined') ? [ subtag, value ] : [ subtag ]
        });
      }
    }

    /**
     * Gets an extension subtag proceeding value by prefix.
     *
     * @param prefix - The prefix to search for.
     * @param subtag - The subtag to look for.  Should be 2 chars long.
     * @param defaultValue - The default value to return if the subtag is found, but does nto have a proceeding value.
     * @returns The string value of the subtag's proceeding value, the `defaultValue` if there is no proceeding value, or
     *     `undefined` if the subtag does not exist.  Note: The default value of `defaultValue` is `undefined` so it may
     *     also return `undefined` even though the subtag actually exists.
     */
    private _getExtension(prefix: string, subtag?: string, defaultValue?: string): string | undefined {
      if (this._lt_extensions.length > 0 && this._lt_extensions.some(v => v.prefix === prefix)) {
        const prefixMatch = this._lt_extensions.filter(v => v.prefix === prefix)[0];
        if (typeof subtag !== 'undefined') {
          const subtagIdx = prefixMatch.subtags.indexOf(subtag);
          if (subtagIdx > -1) {
            if (prefixMatch.subtags.length > subtagIdx + 1) {
              return prefixMatch.subtags[subtagIdx + 1].length > 2 ? prefixMatch.subtags[subtagIdx + 1] : defaultValue;
            }
          }
          return defaultValue;
        } else {
          return prefixMatch.subtags.join('-');
        }
      }
      return undefined;
    }

    /**
     * Trims dashes (`-`) from the begining and end of the string.
     *
     * @param str - The string to remove begining and ending dashes from.
     * @returns The same string without any begining or ending dashes.
     */
    private _trimDashes(str: string): string {
      return XRegExp.replace(str, /(^-+|-+$)/gi, '', 'all');
    }
  }

  /**
   * This is the class that is used to format various items in accordance with a set locale.
   *
   * @remarks
   * This class is meant to be created from the {@link LocalizationService.AppLocalization} class. It should be used to format
   * numbers, currency, dates, times and to sort data.
   *
   */
  export class LocaleFormatter {
    /**
     * This is the default currency name to use if one cannot be determined from the account settings.
     */
    public static get defaultCurrencyName() {
      return 'USD'; // 3 letters, UPPERCASE
    }

    /**
     * This is the default locale to use if one cannot be determined from the account settings. Lazy loaded.
     */
    public static get defaultLocale(): Intl.Locale {
      return IntlLocalePolyfill.LocaleFactory('en-US');
    }

    private _timezone = '';
    private _currencyName = LocaleFormatter.defaultCurrencyName;
    private _locale = LocaleFormatter.defaultLocale;

    /**
     * This is the timezone set for the {@link LocaleFormatter} instance.
     */
    public get timezone(): string {
      return this._timezone;
    }
    public set timezone(timezone: string) {
      this._timezone = timezone;
    }

    /**
     * This is the complete language tag set for the {@link LocaleFormatter} instance.
     */
    public get locale(): Intl.Locale {
      return this._locale;
    }

    /**
     * This is the language set for the {@link LocaleFormatter} instance.
     */
    public get language(): string {
      return this._locale.toString();
    }

    /**
     * This is the region set for the {@link LocaleFormatter} instance.
     */
    public get region(): string | undefined {
      return this._locale.region;
    }

    /**
     * This is the currency name set for the {@link LocaleFormatter} instance.
     */
    public get currencyName(): string {
      return this._currencyName;
    }

    public set currencyName(name: string) {
      this._currencyName = name;
    }

    /**
     * Gets a list of month names based on the current locale.
     */
    public get months(): Array<string> {
      const localeMoment = this.localizeMoment(moment());
      if (localeMoment !== false) {
        return localeMoment.localeData().months();
      } else {
        // we use Intl to get the list
        const monthList: Array<string> = [];
        for (let i = 1; i <= 12; i++) {
          monthList.push(
            (new Intl.DateTimeFormat(this.locale.toString(), { month: 'long' }))
              .format(new Date((new Date()).getFullYear(), i))
          );
        }
        return monthList;
      }
    }

    /**
     * Gets a list of month short names based on the current locale.
     */
    public get monthsShort(): Array<string> {
      const localeMoment = this.localizeMoment(moment());
      if (localeMoment !== false) {
        return localeMoment.localeData().monthsShort();
      } else {
        // we use Intl to get the list
        const monthList: Array<string> = [];
        for (let i = 0; i < 12; i++) {
          monthList.push(
            (new Intl.DateTimeFormat(this.locale.toString(), { month: 'short' }))
              .format(new Date((new Date()).getFullYear(), i))
          );
        }
        return monthList;
      }
    }

    /**
     * Gets a list of month min names based on the current locale.
     */
    public get monthsMin(): Array<string> {
      const localeMoment = this.localizeMoment(moment());
      if (localeMoment !== false) {
        return localeMoment.localeData().monthsShort().map(v => v[0]);
      } else {
        // we use Intl to get the list
        const monthList: Array<string> = [];
        for (let i = 0; i < 12; i++) {
          monthList.push(
            (new Intl.DateTimeFormat(this.locale.toString(), { month: 'narrow' }))
              .format(new Date((new Date()).getFullYear(), i))
          );
        }
        return monthList;
      }
    }

    /**
     * Gets a list of weekday names based on the current locale.
     */
    public get weekdays(): Array<string> {
      const localeMoment = this.localizeMoment(moment());
      if (localeMoment !== false) {
        return localeMoment.localeData().weekdays();
      } else {
        // we use Intl to get the list
        const weekdayList: Array<string> = [];
        const knownSunday = new Date(2020, 3, 1);
        const sundayDateValue = knownSunday.getDate();
        for (let i = 0; i < 7; i++) {
          knownSunday.setDate(sundayDateValue + i);
          weekdayList.push(
            (new Intl.DateTimeFormat(this.locale.toString(), { weekday: 'long' }))
              .format(knownSunday)
            );
        }
        return weekdayList;
      }
    }

    /**
     * Gets a list of weekday short names based on the current locale.
     */
    public get weekdaysShort(): Array<string> {
      const localeMoment = this.localizeMoment(moment());
      if (localeMoment !== false) {
        return localeMoment.localeData().weekdaysShort();
      } else {
        // we use Intl to get the list
        const weekdayList: Array<string> = [];
        const knownSunday = new Date(2020, 3, 1);
        const sundayDateValue = knownSunday.getDate();
        for (let i = 0; i < 7; i++) {
          knownSunday.setDate(sundayDateValue + i);
          weekdayList.push(
            (new Intl.DateTimeFormat(this.locale.toString(), { weekday: 'short' }))
              .format(knownSunday)
            );
        }
        return weekdayList;
      }
    }

    /**
     * Gets a list of weekday minimal names based on the current locale.
     */
    public get weekdaysMin(): Array<string> {
      const localeMoment = this.localizeMoment(moment());
      if (localeMoment !== false) {
        return localeMoment.localeData().weekdaysMin();
      } else {
        // we use Intl to get the list
        const weekdayList: Array<string> = [];
        const knownSunday = new Date(2020, 3, 1);
        const sundayDateValue = knownSunday.getDate();
        for (let i = 0; i < 7; i++) {
          knownSunday.setDate(sundayDateValue + i);
          weekdayList.push(
            (new Intl.DateTimeFormat(this.locale.toString(), { weekday: 'narrow' }))
              .format(knownSunday)
            );
        }
        return weekdayList;
      }
    }

    /**
     * Get a friendly display name for the timezone according to the locale.
     */
    public static timeZoneDisplay(locale: string, timezone: string): string {
      const tmpLocale = new Intl.Locale(locale, {
        numberingSystem: 'latn', // we want to override to use latn numbering only
        hourCycle: 'h23' // this is so the hour is 0-23
      }).toString();
      const tzName = new Intl.DateTimeFormat(
        tmpLocale,
        {
          timeZoneName:'long',
          timeZone: timezone,
          hour: 'numeric',
          hour12: false
        }
      ).format(new Date).replace(
        '' + (new Date((new Date).toLocaleString(
          tmpLocale,
          {
            timeZone: timezone
          }
        ))).getHours(),
        ''
      ).trim();

      // we use en-us here becasue we want to do this in a predictable way.
      const minutesOffset = (
        new Date((new Date).toLocaleString('en-us', { timeZone: timezone })).getTime() -
        new Date((new Date).toLocaleString('en-us', { timeZone: 'UTC' })).getTime()
      ) / 60000;
      const retQuote = 'UTC' +
        (minutesOffset >= 0 ? '+' : '-') +
        ('' + Math.abs(Math.floor(minutesOffset / 60))).padStart(2, '0') +
        ':' +
        ('' + Math.abs((minutesOffset % 60))).padStart(2, '0');

      return '(' + retQuote + ') ' + tzName;
    }

    /**
     * Get a friendly display name for the timezone according to the locale.
     */
    public timeZoneDisplay(timezone: string|null = null): string {
      return LocaleFormatter.timeZoneDisplay(this.locale.toString(), timezone || this.timezone);
    }

    /**
     * Gets the ordinal string of a number based on the locale. 1 -> 1st, 2 -> 2nd, etc.
     *
     * @param num - The number to convert.
     * @returns The ordinal representation of the number based on the current locale.
     */
    public oridinal(num: number, keepNumber = true): string {
      const localeMoment = this.localizeMoment(moment());
      if (localeMoment !== false) {
        return localeMoment.localeData().ordinal(num);
      } else {
        const ordinalSuffixes = new Map([
          ['one', 'st'],
          ['two', 'nd'],
          ['few', 'rd'],
          ['other', 'th']
        ]);
        const pluralRules = new Intl.PluralRules(this.locale.toString(), { type: 'ordinal' });
        return (keepNumber ? num.toString() : '') + ordinalSuffixes.get(pluralRules.select(num));
      }
    }

    /**
     * Sets a new locale from language tag.
     *
     * @param tag - the language tag to parse
     * @param options - locale options to use as overrides.
     */
    public setLocale(tag: string, options?: Intl.LocaleOptions): Intl.Locale {
      return this._locale = IntlLocalePolyfill.LocaleFactory(tag, options);
    }

    /**
     * This will collate an array of strings in alphabetical order in accordance with the locale.
     *
     * @param values - This is the list of strings to sort.
     * @returns The sorted list of strings.
     */
    public collateValues(values: Array<string>): Array<string> {
      return values.sort(this.stringCompare);
    }

    /**
     * This will collate an array of items, using a string value selector, in alphabetical order in accordance with the locale.
     *
     * @param values - This is the list of items to sort.
     * @param selector - The closure used to return a string value that will be used to sort the items by.
     * @returns The sorted list of items.
     * @typeParam TArrayElement - The item type for the array.
     */
    public collateValuesWithSelector<TArrayElement>(
      values: Array<TArrayElement>,
      selector: ((arrayElement: TArrayElement) => string)
    ): Array<TArrayElement> {
      return values.sort((a: TArrayElement, b: TArrayElement) => this.stringCompare(selector(a), selector(b)));
    }

    /**
     * Formats a number in accordance with the locale set.
     *
     * @param number -  The number to format.
     * @param options - A set of options to pass to the number formatter.
     * @returns A string that is the formatted number.
     */
    public formatNumber(number: number, options?: Intl.NumberFormatOptions): string {
      if (typeof options !== 'undefined') {
        options.localeMatcher = 'best fit';
      }
      return (new Intl.NumberFormat(this.locale.toString(), options)).format(number);
    }

    /**
     * Gets the default options used wthen formatting a number.
     *
     * @returns The options object for the number formatter containing the default options used.
     */
    public getDefaultNumberFormatOptions(): Intl.NumberFormatOptions {
      return (new Intl.NumberFormat(this.locale.toString(), { localeMatcher: 'best fit' } )).resolvedOptions();
    }

    /**
     * Formats a number to be a currency representation in accordance with the locale set.
     *
     * @param number -  The number to format to currency.
     * @param currency - The currency name to use. If not specified, or null, it will used the account's specified currency.
     * @param options - A set of options to pass to the number formatter.
     * @returns A string that is the formatted number as a currency representation.
     */
    public formatCurrency(number: number, currency?: string|null, options?: Intl.NumberFormatOptions): string {
      if (typeof options === 'undefined' || options === null) {
        options = this.getDefaultNumberFormatOptions();
      }
      options.localeMatcher = 'best fit';
      options.style = 'currency';
      options.currency = (typeof currency !== 'undefined' && currency !== null && currency.length > 0 ? currency : this.currencyName);
      return this.formatNumber(number, options);
    }

    /**
     * Gets a list of timezones for the country set on this {@link LocaleFormatter} instance.
     *
     * @param with_offset - Whether to include the offset in the return.
     * @returns A list of timezone strings in an array.
     */
    public getTimezones<T extends false>(with_offset?: T ): T extends false ? string[] : never;

    /**
     * Gets a list of timezones for the country set on this {@link LocaleFormatter} instance.
     *
     * @param with_offset - When true, this will also include the offset in the return.
     * @returns A list of timezone objects that have the name of the timezone and the offset.
     */
    public getTimezones<T extends true>(with_offset: T ): T extends true ? moment.MomentZoneOffset[] : never;
    public getTimezones(with_offset?: boolean ): moment.MomentZoneOffset[] | string[] {
      if (this.region && this.region.length > 0) {
        return moment.tz.zonesForCountry(this.region, !!with_offset);
      }
      return [];
    }

    /**
     * Formats a date/time in accordance with the locale set.
     * Keep in mind that the locale may have an alternate calendar set via `-u-ca-XXXXXXXX`.
     *
     * @param date - The date/time to format.
     * @param format - The momentjs localized date/time format string to use.
     * @param options - A set of options for formatting the date.
     *    - `convertToLocalTZ` - If true this will convert the date/time to the local timezone. (default is `true`)
     *    - `forceDateIsUTC` - This will force the value of the `date` to be UTC regardless of the timezone set for it.
     * This is useful for string and numeric values for `date`. If this is false or is not specified,
     * the timezone for the `date` will be the timezone set in the `Date` or `Moment` instance, or
     * the timezone of the client for string and numeric values.
     * @returns A string that is the formatted date/time.
     */
    public formatDateTime(
      date: FormattableDateType,
      format: LocalizedDateTimeFormat,
      options?: { convertToLocalTZ?: boolean, forceDateIsUTC?: boolean }
    ): string {
      // set default values to options
      if (typeof options === 'undefined') {
        options = { convertToLocalTZ: true, forceDateIsUTC: false};
      } else {
        if (typeof options.convertToLocalTZ === 'undefined') {
          options.convertToLocalTZ = true;
        }
        if (typeof options.forceDateIsUTC === 'undefined') {
          options.forceDateIsUTC = false;
        }
      }


      let nonLocaleMoment: moment.Moment;
      if (options.forceDateIsUTC) {
        if (typeof date === 'string' || typeof date === 'number') {
          nonLocaleMoment = moment.utc(date);
        } else if (moment.isMoment(date)) {
          nonLocaleMoment = moment.utc(date.toArray());
        } else {
          // it is a Date object
          nonLocaleMoment = moment.utc([
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            date.getSeconds(),
            date.getMilliseconds()
          ]);
        }
      } else {
        nonLocaleMoment = moment(date);
      }

      if (options.convertToLocalTZ && this.timezone.length > 0) {
        // convert it to the locale defined timezone regardless of what it currently is.
        nonLocaleMoment = nonLocaleMoment.tz(this.timezone);
      }

      // handle special API formats that do not do localization
      switch (format) {
        case 'yyyy-MM-dd':
          return nonLocaleMoment.format('YYYY-MM-DD');
        case 'yyyy':
          return nonLocaleMoment.format('YYYY');
        case 'd':
          return nonLocaleMoment.format('D');
      }

      const localeMoment: moment.Moment | false = this.localizeMoment(nonLocaleMoment);
      if (localeMoment !== false) {
        return localeMoment.format(this.parseCustomMomentTokens(format));
      } else {
        // use the built in Intl to do the formatting because moment did not have a locale that matched
        const dtfoptions: Intl.DateTimeFormatOptions = {};
        switch (format) {
          case 'LT':
          case 'lt':
            dtfoptions.hour12 = true;
            dtfoptions.hour = 'numeric';
            dtfoptions.minute = 'numeric';
          break;
          case 'LTS':
          case 'lts':
            dtfoptions.hour12 = true;
            dtfoptions.hour = 'numeric';
            dtfoptions.minute = 'numeric';
            dtfoptions.second = 'numeric';
          break;
          case 'L LT':
            dtfoptions.year = 'numeric';
            dtfoptions.month = '2-digit';
            dtfoptions.day = '2-digit';
            dtfoptions.hour12 = true;
            dtfoptions.hour = 'numeric';
            dtfoptions.minute = 'numeric';
          break;
          case 'l lt':
            dtfoptions.year = 'numeric';
            dtfoptions.month = 'numeric';
            dtfoptions.day = 'numeric';
            dtfoptions.hour12 = true;
            dtfoptions.hour = 'numeric';
            dtfoptions.minute = 'numeric';
          break;
          case 'L':
            dtfoptions.year = 'numeric';
            dtfoptions.month = '2-digit';
            dtfoptions.day = '2-digit';
          break;
          case 'l':
            dtfoptions.year = 'numeric';
            dtfoptions.month = 'numeric';
            dtfoptions.day = 'numeric';
          break;
          case 'LL':
            dtfoptions.year = 'numeric';
            dtfoptions.month = 'long';
            dtfoptions.day = 'numeric';
          break;
          case 'LLS':
            dtfoptions.month = 'long';
            dtfoptions.day = 'numeric';
          break;
          case 'll':
            dtfoptions.year = 'numeric';
            dtfoptions.month = 'short';
            dtfoptions.day = 'numeric';
          break;
          case 'lls':
            dtfoptions.month = 'short';
            dtfoptions.day = 'numeric';
          break;
          case 'LLL':
            dtfoptions.year = 'numeric';
            dtfoptions.month = 'long';
            dtfoptions.day = 'numeric';
            dtfoptions.hour12 = true;
            dtfoptions.hour = 'numeric';
            dtfoptions.minute = 'numeric';
          break;
          case 'lll':
            dtfoptions.year = 'numeric';
            dtfoptions.month = 'short';
            dtfoptions.day = 'numeric';
            dtfoptions.hour12 = true;
            dtfoptions.hour = 'numeric';
            dtfoptions.minute = 'numeric';
          break;
          case 'LLLL':
            dtfoptions.weekday = 'long';
            dtfoptions.year = 'numeric';
            dtfoptions.month = 'long';
            dtfoptions.day = 'numeric';
            dtfoptions.hour12 = true;
            dtfoptions.hour = 'numeric';
            dtfoptions.minute = 'numeric';
          break;
          case 'llll':
            dtfoptions.weekday = 'short';
            dtfoptions.year = 'numeric';
            dtfoptions.month = 'short';
            dtfoptions.day = 'numeric';
            dtfoptions.hour12 = true;
            dtfoptions.hour = 'numeric';
            dtfoptions.minute = 'numeric';
          break;
          case 'MMMM YYYY':
            dtfoptions.year = 'numeric';
            dtfoptions.month = 'long';
          break;
          case 'MMM YYYY':
            dtfoptions.year = 'numeric';
            dtfoptions.month = 'short';
          break;
          case 'md':
            dtfoptions.month = 'numeric';
            dtfoptions.day = 'numeric';
          break;
          case 'MYY':
            dtfoptions.year = '2-digit';
            dtfoptions.month = 'numeric';
            break;
          case 'mdy':
            dtfoptions.year = '2-digit';
            dtfoptions.month = '2-digit';
            dtfoptions.day = '2-digit';
            dtfoptions.hour12 = true;
            dtfoptions.hour = 'numeric';
            dtfoptions.minute = 'numeric';
          break;
        }
        dtfoptions.localeMatcher = 'best fit';
        return (new Intl.DateTimeFormat(this.locale.toString(), dtfoptions)).format(nonLocaleMoment.toDate());
      }
    }

    public parseCustomMomentTokens(format: LocalizedDateTimeFormat, locale?: string): string {
      if ((DayMonthFormatsArray as Array<string>).includes(format)) {
        switch (format) {
          case 'LLS':
            return XRegExp.replace(this.getLocalizedDateFormat('LL', locale) || 'MMMM Do', /\bYYYY\b/g, '', 'all').trim();
          case 'lls':
            return XRegExp.replace(XRegExp.replace(this.getLocalizedDateFormat('ll', locale) || 'MMM D', /\bYYYY\b/g, '', 'all'), /,/g, '', 'all').trim();
          case 'md':
            return XRegExp.replace(this.getLocalizedDateFormat('l', locale) || 'M/D', /\b\/YYYY\b/g, '', 'all').trim();
          case 'MYY':
            return XRegExp.replace(this.getLocalizedDateFormat('l', locale) || 'M/D/YYYY', /\bD\/YYYY\b/g, 'YY', 'all').trim();
        }
      }
      if ((CompoundLocalizedDateTimeFormatsArray as Array<string>).includes(format)) {
        switch (format) {
          case 'mdy':
            return `${XRegExp.replace(this.getLocalizedDateFormat('l', locale) || 'MM/D/YYYY', /\bYYYY\b/g, 'YY', 'all')}  ${XRegExp.replace(this.getLocalizedDateFormat('lt', locale) || 'h:mm A', /\bYYYY\b/g, '', 'all')}`;
        }
      }
      return format;
    }

    /**
     * Converts date/time to UTC and formats as a server formatted string.
     *
     * @param date - The date/time to convert to server format
     * @param dateOrTime - If `date` it will only return the the date and not the time also.  It will not convert UTC!
     *    If `time` it will only convert the time.  It will convert this from the specified timezone to UTC. If it is
     *    already UTC, then there will be no change.
     * @param dontTZConvert - If this is true, it will not attempt to convert it to UTC.
     * @returns The formatted string that represents the date/time in proper server format.
     */
    public dateTimeToAppFormat(
      date: FormattableDateType,
      dateOrTime: 'date' | 'time' | 'both' = 'both',
      dontTZConvert?: boolean,
      appDateFormat = 'YYYY-MM-DD',
      appTimeFormat = 'HH:mm:ss'
    ): string | null {
      let momentDate: moment.Moment;
      if (moment.isMoment(date)) {
        momentDate = date;
      } else if (moment.isDate(date) || typeof date === 'string' || typeof date === 'number') {
        momentDate = moment(date);
      } else {
        return null;
      }

      // Our BE server/app/api is always en-US locale for dates and times!
      momentDate = momentDate.locale('en-US');
      // const appDateFormat = 'YYYY-MM-DD';
      // const appTimeFormat = 'HH:mm:ss';
      switch (dateOrTime) {
        case 'date':
          if (!dontTZConvert) {
            momentDate = momentDate.utc();
            if (this.timezone.length > 0) {
              momentDate.tz(this.timezone);
            }
          }
          return momentDate.format(appDateFormat);
        case 'time':
          if (!dontTZConvert) {
            momentDate = momentDate.utc();
            if (this.timezone.length > 0) {
              momentDate.tz(this.timezone);
            }
          }
          return momentDate.format(appTimeFormat);
        case 'both':
          if (!dontTZConvert) {
            momentDate = momentDate.utc();
            if (this.timezone.length > 0) {
              momentDate.tz(this.timezone);
            }
          }
          return momentDate.format(`${appDateFormat} ${appTimeFormat}`);
      }
      return null;
    }

    /**
     * Converts date to UTC and formats as a server formatted string.
     *
     * @param date - The date to convert to server format.
     * @param dontTZConvert - If this is true, it will not attempt to convert it to UTC.
     * @returns The formatted string that represents the date in proper server format.
     */
    public dateToAppFormat(
      date: FormattableDateType,
      dontTZConvert?: boolean
    ): string | null {
      return this.dateTimeToAppFormat(date, 'date', dontTZConvert);
    }

    /**
     * Converts time to UTC and formats as a server formatted string.
     *
     * @param time - The time to convert to server format.
     * @param dontTZConvert - If this is true, it will not attempt to convert it to UTC.
     * @returns The formatted string that represents the time in proper server format.
     */
    public timeToAppFormat(
      time: FormattableDateType,
      dontTZConvert?: boolean
    ): string | null {
      return this.dateTimeToAppFormat(time, 'time', dontTZConvert);
    }

    /**
     * Converts a string to upper case based on the locale.
     */
    public stringToUpper(str: string): string {
      return str.toLocaleUpperCase(this.locale.toString());
    }

    /**
     * Converts a string to lower case based on the locale.
     */
    public stringToLower(str: string): string {
      return str.toLocaleLowerCase(this.locale.toString());
    }

    /**
     * Compares two strings according to the sort order of the locale.
     */
    public stringCompare(str1: string, str2: string): number {
      return (new Intl.Collator(this.locale.toString())).compare(str1, str2);
    }

    /**
     * Parses a string for a number using locale rules.
     */
    public stringToNumber(str: string): number | undefined {
      const thousandSeparator = this.formatNumber(1111, { numberingSystem: 'latn' }).replace(/1/g, '');
      const decimalSeparator = this.formatNumber(1.1, { numberingSystem: 'latn' }).replace(/1/g, '');

      // convert the numbering system if they use something different.
      if (typeof this.locale.numberingSystem !== 'undefined' &&
        this.locale.numberingSystem !== 'latn' &&
        this.isNumberingSystem(this.locale.numberingSystem)
      ) {
        str = this.stringConvertNumberingSystem(str, this.locale.numberingSystem, 'latn');
      }
      // remove the thousands seperator and replace the decimal seporator with .
      if (thousandSeparator.length > 0) {
        str = str.replace(new RegExp('\\' + thousandSeparator, 'g'), '');
      }
      if (decimalSeparator.length > 0 && decimalSeparator !== '.') {
        str = str.replace(new RegExp('\\' + decimalSeparator), '.');
      }
      return Number(str);
    }

    /**
     * Converts numerals in a string from one numbering system to another.
     *
     * @param str - The string containing numerals in the `from` numbering system.
     * @param from - The numbering system to convert from.
     * @param to - The numbering system to convert to.
     * @returns The string with the numberals replaced.
     * @example
     * stringConvertNumberingSystem('१२,३४,५६,७८९.००१', 'deva', 'latn'); // -> "12,34,56,789.001"
     */
    public stringConvertNumberingSystem(str: string, from: NumberingSystem, to: NumberingSystem): string {
      if (from === to) {
        return str;
      }

      // first we need to build the number map
      const fromNumerals = this.formatNumber(1234567890, { numberingSystem: from, useGrouping : false, notation: 'standard' }).split('');
      const toNumerals = this.formatNumber(1234567890, { numberingSystem: to, useGrouping : false, notation: 'standard' }).split('');
      const replacements: Array<XRegExp.ReplacementDetail>
        = fromNumerals.map<XRegExp.ReplacementDetail>((fromNumeral, idx) => [ fromNumeral, toNumerals[idx], 'all' ]);

      // then we can replace them all at once.
      return XRegExp.replaceEach(str, replacements);
    }

    public isNumberingSystem(str: string): str is NumberingSystem {
      return str === (str as NumberingSystem);
    }

    public localizeMoment(momentObj: moment.Moment): moment.Moment | false {
      const matchedLocale = this.guessMomentLocale();
      if (typeof matchedLocale !== 'undefined') {
        return momentObj.locale(matchedLocale);
      }
      return false;
    }

    public getLocalizedDateFormat(key: moment.LongDateFormatKey, locale?: string): string | undefined {
      const matchedLocale = locale || this.guessMomentLocale();
      if (typeof matchedLocale !== 'undefined') {
        return moment.localeData(matchedLocale).longDateFormat(key);
      }
      return undefined;
    }

    public guessMomentLocale(): string | undefined {
      // First we see if moment can do it for us, if not we use the built in Intl to do it.
      // Here we build a list of locale variations to see if moment can match to any of them.
      const localeMin = this.locale.minimize();
      const localeMax = this.locale.maximize();
      // These values are added in order of best match. So the lower the index, the better the match.
      const localeVariations = [
        // The current locale as is.
        this.locale.toString(),
      ];
      if (this._stringNotEmpty(this.locale.region)) {
        // The current locale with language and region only
        localeVariations.push(this.locale.language + '-' + this.locale.region);
      }
      if (this._stringNotEmpty(localeMin.region)) {
        // The minimized locale with language and region only
        localeVariations.push(localeMin.language + '-' + localeMin.region);
      }
      if (this._stringNotEmpty(this.locale.region)) {
        // The minimized locale language and current locale region.
        localeVariations.push(localeMin.language + '-' + this.locale.region);
      }
      if (this._stringNotEmpty(localeMax.region)) {
        // The maximized locale language and maximized locale region.
        localeVariations.push(localeMax.language + '-' + localeMax.region);
      }
      if (this._stringNotEmpty(this.locale.region)) {
        // The maximized locale language and current locale region.
        localeVariations.push(localeMax.language + '-' + this.locale.region);
      }
      // Just the language from the current locale
      localeVariations.push(this.locale.language);
      if (localeVariations.indexOf(localeMin.language) === -1) {
        // just the language tag from the minimized locale
        localeVariations.push(localeMin.language);
      }
      if (localeVariations.indexOf(localeMax.language) === -1) {
        // just the language tag from the maximized locale
        localeVariations.push(localeMax.language);
      }

      let matchedLocale: string | undefined;
      for (let index = 0; index < localeVariations.length; index++) {
        matchedLocale = moment.locales().find(v => v.toLowerCase() === localeVariations[index].toLowerCase());
        if (typeof matchedLocale !== 'undefined' && matchedLocale.length > 0) {
          break;
        }
      }
      return matchedLocale;
    }

    private _stringNotEmpty(str: string | undefined | null): str is string {
      if (typeof str === 'undefined' || str === null) {
        return false;
      }
      return str.length > 0;
    }
  }

  /**
   * This class retrieves and maintains the account's and client's locale
   * preferences and provides instances of {@link LocaleFormatter} for each.
   */
  @Injectable({ providedIn: 'root' })
  export class AppLocalization implements OnDestroy {
    /**
     * The account {@link LocaleFormatter} instance.
     *
     * @remarks
     * This is used to get locale information about the account.  It is also
     * used to format various data in accordance with the account's locale.
     */
    public account: LocaleFormatter;

    /**
     * The client {@link LocaleFormatter} instance.
     *
     * @remarks
     * This is used to get locale information about the client.  It is also
     * used to format various data in accordance with the client's locale.
     */
    public client: LocaleFormatter;

    private _rendererFactory2 = inject(RendererFactory2);

    private _renderer: Renderer2;
    private _destroy$ = new Subject<void>();

    constructor() {
      this.account = new LocaleFormatter();
      this.client = new LocaleFormatter();
      this._renderer = this._rendererFactory2.createRenderer(null, null);

      moment.locale(LocaleFormatter.defaultLocale.toString());

      this.updateAccount();

      // subcribe to changes of the browser's language
      this._createLanguageChangeObservable(this._renderer).subscribe(() => {
        this.updateClient();
      });

      this.updateClient();
    }

    ngOnDestroy() {
      this._destroy$.next();
      this._destroy$.complete();
    }

    /**
     * This is used to listen for changes to the client's prefered language.
     *
     * @param renderer - the Angular Renderer2.
     * @returns An observable object attached to the browser's onlanguagechange event.
     */
    private _createLanguageChangeObservable(renderer: Renderer2) {
      let removeLanguageChangeEventListener: () => void;
      const createLanguageChangeEventListener: (handler?: (e: Event) => boolean | void) => void =
        (handler?: (e: Event) => boolean | void) => {
          if (handler != null) {
            removeLanguageChangeEventListener = renderer.listen('window', 'languagechange', handler);
          }
        };

      return fromEventPattern<Event>(
        createLanguageChangeEventListener,
        () => removeLanguageChangeEventListener()
      ).pipe(takeUntil(this._destroy$));
    }

    /**
     * Updates the account {@link LocaleFormatter} instance by calling out to the API for new values
     *
     * @returns void
     */
    public updateAccount(appSettings?: AppLocaleSettings) {
      // get and set timezone and locale
      // these are settings, so we need to hit the api
      if (typeof appSettings !== 'undefined' && appSettings !== null) {
        if (appSettings.locale) {
          this.account.setLocale(appSettings.locale);
        } else {
          // if the server did not specify a locale, use the default locale (should it use the client locale?)
          this.account.setLocale(LocaleFormatter.defaultLocale.toString());
        }

        if (appSettings.timeZone) {
          this.account.timezone = appSettings.timeZone;
        } else {
          // if the server did not specify a timezone, so we will try and guess.
          // we have a languagetag at this point so lets use the region from it to try and guess a timezone
          const timezones = this.account.getTimezones(false);
          if (timezones && timezones.length > 0) {
            if (timezones.length === 1) {
              this.account.timezone = timezones[0];
            } else {
              // We have more than one timezone for the region, so what should we do?
              // We will see if any of the timezones match the client and if so we will use that,
              // otherwise we will just use the first one in the list.
              const clientTZ = moment.tz.guess(true);
              if (timezones.includes(clientTZ)) {
                this.account.timezone = clientTZ;
              } else {
                this.account.timezone = timezones[0];
              }
            }
          } else {
            // if we cannot guess, then we use the client's timezone
            this.account.timezone = moment.tz.guess(true);
          }
        }

        if (appSettings.currencyName) {
          // the account and client LocaleFormatter share the same currency name.
          this.account.currencyName = appSettings.currencyName;
          this.client.currencyName = appSettings.currencyName;
        } else {
          this.account.currencyName = LocaleFormatter.defaultCurrencyName;
          this.client.currencyName = LocaleFormatter.defaultCurrencyName;
        }
      } else {
        this.account.timezone = moment.tz.guess(true);
        this.account.setLocale(LocaleFormatter.defaultLocale.toString());
        this.account.currencyName = LocaleFormatter.defaultCurrencyName;
        this.client.currencyName = LocaleFormatter.defaultCurrencyName;
      }
    }

    /**
     * Updates the client {@link LocaleFormatter} instance by querying the browser for new values.
     *
     * @returns void
     */
    public updateClient(): void {
      // get and set timezone and locale
      this.client.timezone = moment.tz.guess(true);
      if (typeof navigator.languages !== 'undefined' && Array.isArray(navigator.languages) && navigator.languages.length > 0) {
        this.client.setLocale(navigator.languages[0]);
      } else if (typeof navigator.language === 'string' && navigator.language.length > 0) {
        this.client.setLocale(navigator.language);
      } else {
        this.client.setLocale(LocaleFormatter.defaultLocale.toString());
      }
    }

    public guessCurrencyNameFromSymbol(currencySymbol: string, region: string): string | undefined {
      const results = currencyList.filter(cm => cm.symbol === currencySymbol || cm.symbolNative === currencySymbol);
      if (results.length > 0) {
        if (results.length === 1 || region.length < 2) {
          // there was only one result
          return results[0].code;
        }

        const regionResult = results.find(cm => cm.regions.includes(region.substring(0, 2).toUpperCase()));
        if (typeof regionResult !== 'undefined') {
          // we matched a region
          return regionResult.code;
        }

        const nonNativeResult = results.find(cm => cm.symbol === currencySymbol);
        if (typeof nonNativeResult !== 'undefined') {
          // we have a match to a non-native symbol
          return nonNativeResult.code;
        }

        return results[0].code;
      }
      return undefined;
    }

    public currencySymbolFromName(currencyName: string, region?: string): string | undefined {
      if (currencyName.length === 3) {
        const currencyData = currencyList.find(v => v.code === currencyName.toUpperCase());
        if (typeof currencyData !== 'undefined') {
          region = region || '';
          if (region.length >= 2) {
            region.substring(0, 2);
          }
          region = region.toUpperCase();
          return region.length === 2 && currencyData.regions.includes(region) ? currencyData.symbolNative : currencyData.symbol;
        }
      }
      return undefined;
    }
  }